import { api } from './api';
import { Plant, PlantVariety, Genus, Species, CreatePlantInput, ApiResponse } from '../types/plant';

interface ApiPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const plantApi = {
  // Plant operations
  createPlant: async (plantData: CreatePlantInput): Promise<Plant> => {
    const response = await api.post<ApiResponse<Plant>>('/plants', plantData);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create plant');
    }
    return response.data.data;
  },

  getMyPlants: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    hasQrCode?: boolean;
    plantedAfter?: string;
    plantedBefore?: string;
    plantType?: string;
    category?: string;
  }): Promise<{
    plants: Plant[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> => {
    const response = await api.get<
      ApiResponse<{
        plants: Plant[];
        pagination: ApiPagination;
      }>
    >('/plants/my-plants', { params });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch plants');
    }
    return response.data.data;
  },

  getPlant: async (id: string): Promise<Plant> => {
    const response = await api.get<ApiResponse<Plant>>(`/plants/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch plant');
    }
    return response.data.data;
  },

  updatePlant: async (id: string, updates: Partial<CreatePlantInput>): Promise<Plant> => {
    const response = await api.put<ApiResponse<Plant>>(`/plants/${id}`, updates);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update plant');
    }
    return response.data.data;
  },

  deletePlant: async (id: string): Promise<void> => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/plants/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete plant');
    }
  },

  // Plant variety operations
  searchPlantVarieties: async (query: string): Promise<PlantVariety[]> => {
    const response = await api.get<
      ApiResponse<{
        varieties: PlantVariety[];
        pagination: ApiPagination;
      }>
    >('/plant-varieties/search', {
      params: { query, limit: 20 },
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to search varieties');
    }
    return response.data.data.varieties;
  },

  // Genus operations
  getGenera: async (params?: { name?: string; limit?: number }): Promise<Genus[]> => {
    const response = await api.get<ApiResponse<Genus[]>>('/genera', { params });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch genera');
    }
    return response.data.data;
  },

  createGenus: async (name: string): Promise<Genus> => {
    const response = await api.post<ApiResponse<Genus>>('/genera', { name });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create genus');
    }
    return response.data.data;
  },

  // Species operations
  searchSpeciesByGenus: async (genusId: string): Promise<Species[]> => {
    const response = await api.get<ApiResponse<{ species: Species[] }>>('/species', {
      params: { genus_id: genusId, include_genus: true },
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch species');
    }
    return response.data.data.species;
  },

  createSpecies: async (speciesData: { name: string; genusId: string }): Promise<Species> => {
    const response = await api.post<ApiResponse<Species>>('/species', speciesData);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create species');
    }
    return response.data.data;
  },

  // QR Code operations
  generateQRCode: async (
    plantId: string
  ): Promise<{
    qrCodeId: string;
    qrCodeUrl: string;
    qrCodeImage: string;
    plant: Plant;
  }> => {
    const response = await api.get<
      ApiResponse<{
        qrCodeId: string;
        qrCodeUrl: string;
        qrCodeImage: string;
        plant: Plant;
      }>
    >(`/plants/${plantId}/qrcode`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to generate QR code');
    }
    return response.data.data;
  },

  // Public plant view operations
  getPlantByQrCode: async (
    qrCodeId: string
  ): Promise<{
    plant: Plant;
    scientificName: string;
    plantedDateFormatted: string;
    alternativeNames: string;
  }> => {
    const response = await api.get<
      ApiResponse<{
        plant: Plant;
        scientificName: string;
        plantedDateFormatted: string;
        alternativeNames: string;
      }>
    >(`/plants/public/${qrCodeId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch plant');
    }
    return response.data.data;
  },

  // Plant variety creation
  createPlantVariety: async (varietyData: {
    speciesId: string;
    primaryCommonName: string;
    subspecies?: string | null;
    cultivarName?: string | null;
    alternativeNames?: string[];
    plantType?: string | null;
    category?: string | null;
  }): Promise<PlantVariety> => {
    const response = await api.post<ApiResponse<PlantVariety>>('/plant-varieties', varietyData);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create plant variety');
    }
    return response.data.data;
  },

  // Complete plant variety creation with genus/species upsert
  createCompleteVariety: async (varietyData: {
    genusName: string;
    speciesName: string;
    primaryCommonName: string;
    subspecies?: string | null;
    cultivarName?: string | null;
    alternativeNames?: string[];
    plantType?: string | null;
    tags?: string[];
  }): Promise<PlantVariety> => {
    const response = await api.post<ApiResponse<PlantVariety>>(
      '/plant-varieties/complete',
      varietyData
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create plant variety');
    }
    return response.data.data;
  },

  updatePlantVariety: async (
    id: string,
    varietyData: {
      primaryCommonName: string;
      subspecies?: string | null;
      cultivarName?: string | null;
      alternativeNames?: string[];
      plantType?: string | null;
      growthPattern?: string | null;
      tags?: string[];
    }
  ): Promise<PlantVariety> => {
    const response = await api.put<ApiResponse<PlantVariety>>(
      `/plant-varieties/${id}`,
      varietyData
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update plant variety');
    }
    return response.data.data;
  },
};
